import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { format } from "date-fns";
import { PanelRightOpen } from "lucide-react";
import { Thumbnail } from "../../Thumbnail";
import { HeaderIconButton } from "../../Header/HeaderActions";
import { PanelHeader } from "../../PanelHeader";
import StandardQueuePane from "../../FileQueue/StandardQueuePane";
import TimePickerContent from "../../ContextPanel/TimePickerContent";
import AssistantPane from "../../Editor/AssistantPane";
import ThreePaneWorkspace from "../../Workspace/ThreePaneWorkspace";
import { useAppContext } from "../../../contexts/AppContext";
import { useBasePath } from "../../../contexts/BasePathContext";
import { useFileListProjection } from "../../../hooks/useFileListProjection";
import { useFiles } from "../../../hooks/useFiles";
import { useIsMobile } from "../../../hooks/useMobile";
import { useSelection } from "../../../hooks/useSelection";
import { toast } from "../../ui";
import {
  TIMELINE_PRESETS,
  buildTimelineAxisModel,
  centerRangeAt,
  formatDropTimestamp,
  setRangeDurationAroundDate,
  snapDateToResolutionLocal,
} from "../timeline/engine";
import { useTimelineViewport } from "../timeline/hooks/useTimelineViewport";
import TimelineV3Header, {
  TimelineV3HeaderRightControls,
} from "./TimelineV3Header";
import {
  buildTimelineV3DisplayRows,
  buildTimelineV3Rows,
  getVisibleTimelineV3Rows,
  resolveTimelineV3RowHeight,
  resolveTimelineV3ThumbnailSizeForDuration,
} from "./timelineV3Model";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const COMBINED_TIME_RAIL_WIDTH_PX = 152;
const TIMELINE_OVERSCAN_PX = 180;
const DISPLAY_ROW_GROUP_GAP_FACTOR = 0.55;
const RAIL_ROW_LABEL_MIN_HEIGHT_PX = 40;
const RAIL_LABEL_COLLISION_PADDING_PX = 8;
const RAIL_TICK_LABEL_MIN_HEIGHT_PX = 22;
const TIMELINE_NAVIGATION_TOAST_ID = "timeline-navigation-boundary";
const TIMELINE_WHEEL_ZOOM_INTENSITY = 0.1;
const TIMELINE_WHEEL_ZOOM_MAX_DELTA = 120;
const ROW_LABEL_FORMATS = {
  minute: "EEE, MMM d, yyyy h:mm a",
  hour: "EEE, MMM d, yyyy h a",
  day: "EEE, MMM d, yyyy",
  week: "MMM d, yyyy",
  month: "MMMM yyyy",
  year: "yyyy",
};
const RAIL_ANCHOR_FORMATS = {
  minute: "h:mm a",
  hour: "h a",
  day: "MMM d",
  week: "MMM d",
  month: "MMM yyyy",
  year: "yyyy",
};
const EMPTY_DROP_STATE = Object.freeze({
  active: false,
  positionPct: null,
  snappedDate: null,
});

function getPresetStepMs(preset, large = false) {
  const unit = preset?.clusterBucket?.unit || "day";
  const step = Math.max(1, preset?.clusterBucket?.step || 1);
  const multiplier = large ? 6 : 1;

  if (unit === "minute") return step * multiplier * 60 * 1000;
  if (unit === "hour") return step * multiplier * HOUR_MS;
  if (unit === "week") return step * multiplier * 7 * DAY_MS;
  if (unit === "month") return step * multiplier * 30 * DAY_MS;
  if (unit === "year") return step * multiplier * 365 * DAY_MS;
  return step * multiplier * DAY_MS;
}

function getZoomPresetDelta(activePreset, direction) {
  const currentIndex = TIMELINE_PRESETS.findIndex(
    (preset) => preset.id === activePreset?.id,
  );
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  const nextIndex = Math.max(
    0,
    Math.min(TIMELINE_PRESETS.length - 1, safeIndex + direction),
  );
  return TIMELINE_PRESETS[nextIndex];
}

function clampWheelDelta(deltaY) {
  return Math.max(
    -TIMELINE_WHEEL_ZOOM_MAX_DELTA,
    Math.min(TIMELINE_WHEEL_ZOOM_MAX_DELTA, deltaY),
  );
}

function formatRailGroupLabel(startDate, endDate, unit) {
  if (!startDate || !endDate) return "";
  if (unit === "year") return `${format(startDate, "yyyy")} - ${format(endDate, "yyyy")}`;
  if (unit === "month") {
    if (format(startDate, "yyyy") === format(endDate, "yyyy")) {
      return `${format(startDate, "MMM")} - ${format(endDate, "MMM yyyy")}`;
    }
    return `${format(startDate, "MMM yyyy")} - ${format(endDate, "MMM yyyy")}`;
  }
  if (unit === "minute" || unit === "hour") {
    if (format(startDate, "MMM d") === format(endDate, "MMM d")) {
      return `${format(startDate, "MMM d, h a")} - ${format(endDate, "h a")}`;
    }
    return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d")}`;
  }
  if (format(startDate, "MMM yyyy") === format(endDate, "MMM yyyy")) {
    return `${format(startDate, "MMM d")} - ${format(endDate, "d")}`;
  }
  return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d")}`;
}

function getRailLabel(row, fallbackFormat) {
  if (row.isGroup) {
    return formatRailGroupLabel(
      row.startBucketDate,
      row.endBucketDate,
      row.bucketUnit || "day",
    );
  }
  return format(row.bucketDate, fallbackFormat);
}

function getRowTitle(row, fallbackFormat) {
  const photoCount = row.photos.length;
  const photoLabel = `${photoCount} photo${photoCount === 1 ? "" : "s"}`;
  if (!row.isGroup) {
    return `${format(row.bucketDate, fallbackFormat)} - ${photoLabel}`;
  }

  return `${format(row.startBucketDate, fallbackFormat)} - ${format(
    row.endBucketDate,
    fallbackFormat,
  )} - ${photoLabel}`;
}

function buildRailOccupiedRanges(rows, rowHeight, getTimestampTopPx) {
  return rows.map((row) => {
    const topPx = Number.isFinite(row.topPx)
      ? row.topPx
      : getTimestampTopPx(row.bucketMs);
    const halfHeight =
      Math.max(RAIL_ROW_LABEL_MIN_HEIGHT_PX, rowHeight) / 2 +
      RAIL_LABEL_COLLISION_PADDING_PX;
    return {
      start: topPx - halfHeight,
      end: topPx + halfHeight,
    };
  });
}

function collidesWithOccupiedRanges(topPx, ranges) {
  return ranges.some((range) => topPx >= range.start && topPx <= range.end);
}

function buildVisibleRailTickLabels(labels, occupiedRanges) {
  const labelRanges = [...occupiedRanges];

  return labels.filter((label) => {
    if (collidesWithOccupiedRanges(label.x, labelRanges)) return false;

    labelRanges.push({
      start: label.x - RAIL_TICK_LABEL_MIN_HEIGHT_PX / 2,
      end: label.x + RAIL_TICK_LABEL_MIN_HEIGHT_PX / 2,
    });
    return true;
  });
}

function TimelineV3Row({
  row,
  topPx,
  thumbnailSize,
  rowHeight,
  selectedIndices,
  dragOverPhotoIndex,
  onFileDragStart,
  onFileDragEnd,
  onSelectFile,
  onEditPhoto,
  onRowSelect,
  onTimelineDragOver,
  onPhotoDragOver,
  onPhotoDragLeave,
  onPhotoDrop,
}) {
  return (
    <div
      className="group/row absolute left-0 min-w-max"
      style={{
        top: `${topPx}px`,
        minHeight: `${rowHeight}px`,
        transform: "translateY(-50%)",
      }}
      onClick={() => onRowSelect(row)}
      onDragOver={(event) => {
        onTimelineDragOver(event);
      }}
    >
      <div className="flex min-w-max items-center gap-2 px-4 py-2">
        {row.photos.map((item) => {
          const selected = selectedIndices.has(item.index);
          const isDropTarget = dragOverPhotoIndex === item.index;
          return (
            <button
              key={`timeline-v3-photo-${item.index}`}
              type="button"
              draggable
              onDragStart={(event) => onFileDragStart(event, item.index)}
              onDragEnd={onFileDragEnd}
              onDragOver={(event) => onPhotoDragOver(event, item.index)}
              onDragLeave={(event) => onPhotoDragLeave(event, item.index)}
              onDrop={(event) => onPhotoDrop(event, item, row)}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onSelectFile(item.index, event, item.timelineRow || row);
              }}
              onDoubleClick={() => onEditPhoto(item.index)}
              className={`rounded transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50 ${
                isDropTarget
                  ? "ring-2 ring-accent-primary ring-offset-2 ring-offset-bg-primary"
                  : ""
              }`}
              title={`${item.file.name} • ${format(item.date, "EEE, MMM d, yyyy h:mm:ss a")}`}
            >
              <Thumbnail
                src={item.file.thumbnailUrl}
                alt={item.file.name}
                size={thumbnailSize}
                selected={selected}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TimelineV3TimeRail({
  axisModel,
  rows,
  canvasHeightPx,
  rowHeight,
  todayTopPx,
  activeRowId,
  getTimestampTopPx,
  onTimelineDragOver,
  onTimelineDrop,
  onRowSelect,
}) {
  const railOccupiedRanges = useMemo(
    () => buildRailOccupiedRanges(rows, rowHeight, getTimestampTopPx),
    [rows, rowHeight, getTimestampTopPx],
  );
  const visibleSections = useMemo(
    () =>
      axisModel.sections.filter(
        (section) =>
          section.widthPx >= 72 &&
          !collidesWithOccupiedRanges(section.startX + 12, railOccupiedRanges),
      ),
    [axisModel.sections, railOccupiedRanges],
  );
  const visibleMajorLabels = useMemo(
    () => buildVisibleRailTickLabels(axisModel.majorLabels, railOccupiedRanges),
    [axisModel.majorLabels, railOccupiedRanges],
  );
  return (
    <div
      className="sticky left-0 z-30 h-full shrink-0 bg-bg-primary/95"
      style={{ width: `${COMBINED_TIME_RAIL_WIDTH_PX}px` }}
    >
      <div className="relative h-full" style={{ height: `${canvasHeightPx}px` }}>
        <div className="absolute bottom-0 left-[22px] top-0 w-px bg-border-subtle" />

        {visibleSections.map((section) => (
          <div
            key={section.key}
            className="absolute left-10 right-3 py-1"
            style={{
              top: `${section.startX}px`,
              height: `${Math.max(18, section.widthPx)}px`,
            }}
          >
            <div className="truncate text-[11px] font-medium text-text-supporting">
              {section.label}
            </div>
          </div>
        ))}

        {axisModel.minorTicks.map((tick) => (
          <div
            key={`timeline-v3-rail-minor-tick-${tick.ts}`}
            className="absolute left-[22px] h-px w-2 bg-border-subtle/70"
            style={{ top: `${tick.x}px` }}
          />
        ))}

        {axisModel.majorTicks.map((tick) => (
          <div
            key={`timeline-v3-rail-tick-${tick.ts}`}
            className="absolute left-[22px] h-px w-4 bg-border-subtle"
            style={{ top: `${tick.x}px` }}
          />
        ))}

        {visibleMajorLabels.map((label) => (
          <div
            key={`timeline-v3-rail-major-label-${label.ts}`}
            className="absolute left-10 right-3 -translate-y-1/2 truncate text-[10px] font-medium tabular-nums text-text-supporting"
            style={{ top: `${label.x}px` }}
          >
            {label.label}
          </div>
        ))}

        {rows.map((row) => {
          const topPx = Number.isFinite(row.topPx)
            ? row.topPx
            : getTimestampTopPx(row.bucketMs);
          const unit = row.bucketUnit || "day";
          const railFormat = RAIL_ANCHOR_FORMATS[unit] || RAIL_ANCHOR_FORMATS.day;
          const exactFormat = ROW_LABEL_FORMATS[unit] || ROW_LABEL_FORMATS.day;
          const active =
            row.id === activeRowId ||
            row.sourceRows?.some((sourceRow) => sourceRow.id === activeRowId);
          const labelHeightPx = Math.max(RAIL_ROW_LABEL_MIN_HEIGHT_PX, rowHeight);
          const railLabel = getRailLabel(row, railFormat);
          const rowTitle = getRowTitle(row, exactFormat);

          return (
            <button
              key={`timeline-v3-rail-row-${row.id}`}
              type="button"
              className={`absolute inset-x-2 flex items-center gap-2 rounded-md px-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50 ${
                active
                  ? "bg-bg-tertiary/70 text-text-primary"
                  : "text-text-secondary hover:bg-bg-secondary/70 hover:text-text-primary"
              }`}
              style={{
                top: `${topPx}px`,
                height: `${labelHeightPx}px`,
                transform: "translateY(-50%)",
              }}
              title={rowTitle}
              onClick={() => onRowSelect(row)}
              onDragOver={(event) => {
                onTimelineDragOver(event);
              }}
              onDrop={onTimelineDrop}
            >
              <span
                className={`size-2.5 shrink-0 rounded-full border-2 ${
                  active
                    ? "border-accent-primary bg-accent-primary"
                    : "border-bg-primary bg-text-decorative"
                }`}
              />
              <span className="min-w-0">
                <span className="block truncate text-[11px] font-semibold tabular-nums">
                  {railLabel}
                </span>
                <span className="block text-[10px] text-text-supporting">
                  {row.photos.length} photo{row.photos.length === 1 ? "" : "s"}
                </span>
              </span>
            </button>
          );
        })}

        {todayTopPx !== null && (
          <div
            className="absolute left-[22px] right-0 h-px bg-accent-info/70"
            style={{ top: `${todayTopPx}px` }}
          >
            <div className="absolute left-4 -translate-y-1/2 rounded-sm border border-border-control/60 bg-bg-primary/95 px-1 text-[10px] font-semibold text-accent-info">
              Today
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineV3View() {
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const isMobile = useIsMobile();
  const {
    files: allFiles,
    fileListSort,
    fileListFilters,
    timezone,
    calendarSelectedDate,
    calendarSelectedHour,
    setCalendarSelectedDate,
    setCalendarSelectedHour,
    setCalendarPanelExpanded,
    calendarRightPanelOpen,
    setCalendarRightPanelOpen,
    calendarMobileLeftOpen,
    setCalendarMobileLeftOpen,
    calendarMobileRightOpen,
    setCalendarMobileRightOpen,
    setKeyboardShortcutsModalOpen,
    setKeyboardShortcutsInitialView,
    setPrimaryIndex,
  } = useAppContext();
  const {
    selectedIndices,
    primaryIndex,
    selectSingle,
    toggleSelect,
    rangeSelect,
  } = useSelection();
  const { assignFilesDate } = useFiles();
  const { visibleIndices } = useFileListProjection({
    files: allFiles,
    sort: fileListSort,
    filters: fileListFilters,
  });
  const { files, sourceIndices } = useMemo(
    () =>
      visibleIndices.reduce(
        (acc, index) => {
          const file = allFiles[index];
          if (!file) return acc;
          acc.files.push(file);
          acc.sourceIndices.push(index);
          return acc;
        },
        { files: [], sourceIndices: [] },
      ),
    [allFiles, visibleIndices],
  );
  const containerRef = useRef(null);
  const scrollerRef = useRef(null);
  const timelineTrackRef = useRef(null);
  const keyboardPrimaryIndexRef = useRef(primaryIndex);
  const isPanningRef = useRef(false);
  const lastPointerYRef = useRef(0);
  const pendingPanMsRef = useRef(0);
  const panRafRef = useRef(null);
  const [viewportHeightPx, setViewportHeightPx] = useState(1);
  const [dragOverPhotoIndex, setDragOverPhotoIndex] = useState(null);
  const [dropState, setDropState] = useState(EMPTY_DROP_STATE);
  const {
    range,
    setRange,
    presetMarkers,
    activePreset,
    sliderValue,
    setSliderValue,
    goToday,
    panByMs,
  } = useTimelineViewport();
  const hasAutoFitted = useRef(false);

  const durationMs = Math.max(1, range.end.getTime() - range.start.getTime());
  const thumbnailSize = resolveTimelineV3ThumbnailSizeForDuration(durationMs);
  const rowHeight = resolveTimelineV3RowHeight(thumbnailSize);
  const allRows = useMemo(() => {
    const rows = buildTimelineV3Rows(files, sourceIndices, activePreset);
    return rows.map((row) => ({
      ...row,
      bucketUnit: activePreset?.clusterBucket?.unit || "day",
    }));
  }, [files, sourceIndices, activePreset]);
  const visibleRows = useMemo(
    () => getVisibleTimelineV3Rows(allRows, range),
    [allRows, range],
  );
  const canvasHeightPx = Math.max(1, viewportHeightPx);
  const msPerPixel = durationMs / canvasHeightPx;
  const trackOriginMs = range.start.getTime();
  const getTimestampTopPx = useCallback(
    (timestampMs) => (timestampMs - trackOriginMs) / msPerPixel,
    [trackOriginMs, msPerPixel],
  );
  const axisModel = useMemo(
    () =>
      buildTimelineAxisModel({
        range,
        preset: activePreset,
        trackOriginMs,
        msPerPixel,
        viewportWidthPx: canvasHeightPx,
        overscanPx: TIMELINE_OVERSCAN_PX,
      }),
    [range, activePreset, trackOriginMs, msPerPixel, canvasHeightPx],
  );
  const todayTopPx = useMemo(() => {
    const now = Date.now();
    const top = getTimestampTopPx(now);
    return top >= -TIMELINE_OVERSCAN_PX && top <= canvasHeightPx + TIMELINE_OVERSCAN_PX
      ? top
      : null;
  }, [getTimestampTopPx, canvasHeightPx]);
  const displayRows = useMemo(
    () =>
      buildTimelineV3DisplayRows(visibleRows, {
        getTimestampTopPx,
        minGroupGapPx: Math.max(40, Math.min(72, rowHeight * DISPLAY_ROW_GROUP_GAP_FACTOR)),
      }),
    [visibleRows, getTimestampTopPx, rowHeight],
  );
  const rowByPhotoIndex = useMemo(() => {
    const map = new Map();
    allRows.forEach((row) => {
      row.photos.forEach((photo) => map.set(photo.index, row));
    });
    return map;
  }, [allRows]);

  useEffect(() => {
    if (isMobile) {
      navigate(appPath("/calendar"), { replace: true });
    }
  }, [isMobile, navigate, appPath]);

  useEffect(() => {
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    keyboardPrimaryIndexRef.current = primaryIndex;
  }, [primaryIndex]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  }, [range.start, range.end]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.max(1, scroller.clientHeight);
      setViewportHeightPx((prev) =>
        Math.abs(prev - nextHeight) > 0.5 ? nextHeight : prev,
      );
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  const queuePanByMs = useCallback(
    (deltaMs) => {
      pendingPanMsRef.current += deltaMs;
      if (panRafRef.current) return;

      const flushPan = () => {
        const step = pendingPanMsRef.current;
        pendingPanMsRef.current = 0;
        panRafRef.current = null;
        if (Math.abs(step) >= 0.1) {
          panByMs(step);
        }
      };

      panRafRef.current = requestAnimationFrame(flushPan);
    },
    [panByMs],
  );

  useEffect(
    () => () => {
      if (panRafRef.current) {
        cancelAnimationFrame(panRafRef.current);
        panRafRef.current = null;
      }
      pendingPanMsRef.current = 0;
    },
    [],
  );

  const fitToPhotos = useCallback(() => {
    if (!allRows.length) return;
    const first = allRows[0].bucketMs;
    const last = allRows[allRows.length - 1].bucketMs;
    const pad = Math.max(getPresetStepMs(activePreset, true), (last - first) * 0.15);
    setRange({
      start: new Date(first - pad),
      end: new Date(last + pad),
    });
  }, [allRows, activePreset, setRange]);

  useEffect(() => {
    if (hasAutoFitted.current || allRows.length === 0) return;
    hasAutoFitted.current = true;
    fitToPhotos();
  }, [allRows, fitToPhotos]);

  const syncPanelToRow = useCallback(
    (row) => {
      if (!row) return;
      const targetRow = row.sourceRows?.[0] || row;
      setCalendarSelectedDate(format(targetRow.bucketDate, "yyyy-MM-dd"));
      const unit = targetRow.bucketUnit || activePreset?.clusterBucket?.unit;
      setCalendarSelectedHour(
        unit === "minute" || unit === "hour" ? targetRow.bucketDate.getHours() : null,
      );
      setCalendarPanelExpanded(true);
      setCalendarRightPanelOpen(true);
    },
    [
      activePreset,
      setCalendarSelectedDate,
      setCalendarSelectedHour,
      setCalendarPanelExpanded,
      setCalendarRightPanelOpen,
    ],
  );

  const selectPhoto = useCallback(
    (index, event, row) => {
      if (event?.shiftKey && primaryIndex >= 0 && primaryIndex !== index) {
        rangeSelect(primaryIndex, index);
      } else if (event?.metaKey || event?.ctrlKey) {
        toggleSelect(index);
      } else {
        selectSingle(index);
      }
      syncPanelToRow(row || rowByPhotoIndex.get(index));
    },
    [primaryIndex, rangeSelect, toggleSelect, selectSingle, syncPanelToRow, rowByPhotoIndex],
  );

  const handleFileDragStart = useCallback(
    (event, index) => {
      event.dataTransfer.effectAllowed = "move";
      if (selectedIndices.has(index)) {
        event.dataTransfer.setData("fileIndices", JSON.stringify(Array.from(selectedIndices)));
        return;
      }
      selectSingle(index);
      event.dataTransfer.setData("fileIndices", JSON.stringify([index]));
    },
    [selectedIndices, selectSingle],
  );

  const handleFileDragEnd = useCallback(() => {
    setDragOverPhotoIndex(null);
    setDropState(EMPTY_DROP_STATE);
  }, []);

  const getDroppedFileIndices = useCallback((event) => {
    const payload = event.dataTransfer.getData("fileIndices");
    if (!payload) return [];

    try {
      const indices = JSON.parse(payload);
      return Array.isArray(indices) ? indices.filter(Number.isInteger) : [];
    } catch (error) {
      console.error("Invalid drag payload in timeline drop", error);
      return [];
    }
  }, []);

  const updateDropFromPointer = useCallback(
    (clientY) => {
      const rect = timelineTrackRef.current?.getBoundingClientRect();
      if (!rect || rect.height <= 0) return null;

      const ratio = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
      const rawDate = new Date(range.start.getTime() + ratio * durationMs);
      const snappedDate = snapDateToResolutionLocal(
        rawDate,
        activePreset?.assignmentSnap,
      );
      return {
        positionPct: ratio * 100,
        snappedDate,
      };
    },
    [activePreset, durationMs, range],
  );

  const handleTimelineDragOver = useCallback(
    (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";

      const update = updateDropFromPointer(event.clientY);
      if (!update) return;

      setDropState((previousState) => {
        const previousMs = previousState.snappedDate?.getTime() ?? null;
        const nextMs = update.snappedDate?.getTime() ?? null;
        const samePosition =
          previousState.positionPct !== null &&
          Math.abs(previousState.positionPct - update.positionPct) < 0.1;

        if (previousState.active && samePosition && previousMs === nextMs) {
          return previousState;
        }

        return { active: true, ...update };
      });
    },
    [updateDropFromPointer],
  );

  const handleTimelineDragLeave = useCallback((event) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    setDragOverPhotoIndex(null);
    setDropState(EMPTY_DROP_STATE);
  }, []);

  const handleTimelineDrop = useCallback(
    (event) => {
      event.preventDefault();
      setDragOverPhotoIndex(null);

      const update = updateDropFromPointer(event.clientY);
      const indices = getDroppedFileIndices(event);
      if (!update || !indices.length) {
        setDropState(EMPTY_DROP_STATE);
        return;
      }

      assignFilesDate(indices, update.snappedDate);
      setDropState(EMPTY_DROP_STATE);
    },
    [assignFilesDate, getDroppedFileIndices, updateDropFromPointer],
  );

  const handlePhotoDragOver = useCallback((event, index) => {
    event.stopPropagation();
    setDragOverPhotoIndex((previousIndex) =>
      previousIndex === index ? previousIndex : index,
    );
    handleTimelineDragOver(event);
  }, [handleTimelineDragOver]);

  const handlePhotoDragLeave = useCallback((event, index) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    setDragOverPhotoIndex((previousIndex) =>
      previousIndex === index ? null : previousIndex,
    );
  }, []);

  const handlePhotoDrop = useCallback(
    (event) => {
      event.stopPropagation();
      handleTimelineDrop(event);
    },
    [handleTimelineDrop],
  );

  const handleEditPhoto = useCallback(
    (fileIndex) => {
      setPrimaryIndex(fileIndex);
      navigate(appPath("/editor"));
    },
    [setPrimaryIndex, navigate, appPath],
  );

  const showNavigationBoundary = useCallback((message) => {
    toast.info(message, {
      id: TIMELINE_NAVIGATION_TOAST_ID,
      duration: 1200,
    });
  }, []);

  const selectTimelinePhoto = useCallback(
    (photo, row, centerRow = false) => {
      const sourceRow = photo.timelineRow || row;
      keyboardPrimaryIndexRef.current = photo.index;
      selectSingle(photo.index);
      syncPanelToRow(sourceRow);
      if (centerRow) {
        setRange(centerRangeAt(sourceRow.bucketDate, durationMs));
      }
    },
    [durationMs, selectSingle, setRange, syncPanelToRow],
  );

  const moveSelectionWithinRow = useCallback(
    (direction) => {
      if (!displayRows.length) return;

      const currentPrimaryIndex = keyboardPrimaryIndexRef.current;
      const currentRow = displayRows.find((row) =>
        row.photos.some((photo) => photo.index === currentPrimaryIndex),
      );

      if (!currentRow) {
        const fallbackRow =
          direction < 0 ? displayRows[displayRows.length - 1] : displayRows[0];
        const fallbackPhoto =
          direction < 0
            ? fallbackRow.photos[fallbackRow.photos.length - 1]
            : fallbackRow.photos[0];
        selectTimelinePhoto(fallbackPhoto, fallbackRow, true);
        return;
      }

      const currentPhotoPosition = currentRow.photos.findIndex(
        (photo) => photo.index === currentPrimaryIndex,
      );
      const nextPhotoPosition = currentPhotoPosition + direction;

      if (nextPhotoPosition < 0 || nextPhotoPosition >= currentRow.photos.length) {
        showNavigationBoundary(
          direction < 0
            ? "No previous photo in this row."
            : "No next photo in this row.",
        );
        return;
      }

      selectTimelinePhoto(currentRow.photos[nextPhotoPosition], currentRow);
    },
    [displayRows, selectTimelinePhoto, showNavigationBoundary],
  );

  const moveSelectionToRow = useCallback(
    (direction) => {
      if (!allRows.length) return;

      const currentPrimaryIndex = keyboardPrimaryIndexRef.current;
      const currentRowIndex = allRows.findIndex((row) =>
        row.photos.some((photo) => photo.index === currentPrimaryIndex),
      );

      if (currentRowIndex === -1) {
        const fallbackRow =
          direction < 0 ? allRows[allRows.length - 1] : allRows[0];
        const fallbackPhoto =
          direction < 0
            ? fallbackRow.photos[fallbackRow.photos.length - 1]
            : fallbackRow.photos[0];
        selectTimelinePhoto(fallbackPhoto, fallbackRow, true);
        return;
      }

      const nextRowIndex = currentRowIndex + direction;
      if (nextRowIndex < 0 || nextRowIndex >= allRows.length) {
        showNavigationBoundary(
          direction < 0
            ? "Already at the first timeline row."
            : "Already at the last timeline row.",
        );
        return;
      }

      const currentRow = allRows[currentRowIndex];
      const nextRow = allRows[nextRowIndex];
      const currentPhotoPosition = currentRow.photos.findIndex(
        (photo) => photo.index === currentPrimaryIndex,
      );
      const nextPhotoPosition = Math.max(
        0,
        Math.min(nextRow.photos.length - 1, currentPhotoPosition),
      );

      selectTimelinePhoto(nextRow.photos[nextPhotoPosition], nextRow, true);
    },
    [allRows, selectTimelinePhoto, showNavigationBoundary],
  );

  const removeSelectedDateTaken = useCallback(() => {
    const fileIndices =
      selectedIndices.size > 0
        ? Array.from(selectedIndices)
        : primaryIndex >= 0
          ? [primaryIndex]
          : [];

    if (!fileIndices.length) return;
    assignFilesDate(fileIndices, null);
  }, [assignFilesDate, primaryIndex, selectedIndices]);

  const onKeyDown = useCallback(
    (event) => {
      const target = event.target;
      const isInInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable;
      if (isInInput) return;

      if (
        (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
        !event.altKey &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        moveSelectionWithinRow(direction);
      } else if (
        (event.key === "ArrowUp" || event.key === "ArrowDown") &&
        !event.altKey &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        const direction = event.key === "ArrowUp" ? -1 : 1;
        panByMs(direction * durationMs * (event.shiftKey ? 0.8 : 0.2));
      } else if (
        (event.key === "ArrowUp" || event.key === "ArrowDown") &&
        !event.altKey &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();
        const direction = event.key === "ArrowUp" ? -1 : 1;
        moveSelectionToRow(direction);
      } else if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        const nextPreset = getZoomPresetDelta(activePreset, -1);
        setRange(centerRangeAt(new Date((range.start.getTime() + range.end.getTime()) / 2), nextPreset.durationMs));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        const nextPreset = getZoomPresetDelta(activePreset, 1);
        setRange(centerRangeAt(new Date((range.start.getTime() + range.end.getTime()) / 2), nextPreset.durationMs));
      } else if (event.key === "t" || event.key === "T") {
        event.preventDefault();
        goToday();
      } else if (event.key === "f" || event.key === "F") {
        event.preventDefault();
        fitToPhotos();
      } else if (
        (event.key === "Backspace" || event.key === "Delete") &&
        primaryIndex >= 0
      ) {
        event.preventDefault();
        removeSelectedDateTaken();
      } else if (event.key === "Enter" && primaryIndex >= 0) {
        event.preventDefault();
        handleEditPhoto(primaryIndex);
      }
    },
    [
      activePreset,
      durationMs,
      fitToPhotos,
      goToday,
      handleEditPhoto,
      moveSelectionToRow,
      moveSelectionWithinRow,
      panByMs,
      primaryIndex,
      range,
      removeSelectedDateTaken,
      setRange,
    ],
  );

  const zoomTimelineFromWheel = useCallback(
    (event) => {
      const scroller = scrollerRef.current;
      const rect = scroller?.getBoundingClientRect();
      const anchorRatio =
        rect && rect.height > 0
          ? Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
          : 0.5;
      const clampedDelta = clampWheelDelta(event.deltaY);

      setRange((previousRange) => {
        const previousDurationMs = Math.max(
          1,
          previousRange.end.getTime() - previousRange.start.getTime(),
        );
        const anchorMs =
          previousRange.start.getTime() + anchorRatio * previousDurationMs;
        const nextDurationMs =
          previousDurationMs *
          Math.exp(clampedDelta * TIMELINE_WHEEL_ZOOM_INTENSITY);

        return setRangeDurationAroundDate(
          previousRange,
          nextDurationMs,
          new Date(anchorMs),
        );
      });
    },
    [setRange],
  );

  useEffect(() => {
    const handleNativeWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      if (!containerRef.current?.contains(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      zoomTimelineFromWheel(event);
    };

    window.addEventListener("wheel", handleNativeWheel, {
      capture: true,
      passive: false,
    });
    return () => {
      window.removeEventListener("wheel", handleNativeWheel, {
        capture: true,
      });
    };
  }, [zoomTimelineFromWheel]);

  const handleTimelineWheel = useCallback(
    (event) => {
      const scroller = scrollerRef.current;
      const rect = scroller?.getBoundingClientRect();

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        if (scroller) scroller.scrollTop = 0;
        zoomTimelineFromWheel(event);
        return;
      }

      event.preventDefault();
      if (scroller) scroller.scrollTop = 0;

      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        if (scroller) {
          scroller.scrollLeft += event.deltaX;
        }
        return;
      }

      const axisPx = Math.max(1, rect?.height || canvasHeightPx);
      queuePanByMs(event.deltaY * (durationMs / axisPx));
    },
    [canvasHeightPx, durationMs, queuePanByMs, zoomTimelineFromWheel],
  );

  const handleTimelineMouseDown = useCallback((event) => {
    if (
      event.button !== 0 ||
      event.target.closest("[draggable='true']") ||
      event.target.closest("button") ||
      event.target.closest("a") ||
      event.target.closest("input") ||
      event.target.closest("textarea") ||
      event.target.closest("[role='slider']")
    ) {
      return;
    }

    isPanningRef.current = true;
    lastPointerYRef.current = event.clientY;
    if (scrollerRef.current) {
      scrollerRef.current.style.cursor = "grabbing";
    }
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isPanningRef.current) return;

      const scroller = scrollerRef.current;
      const rect = scroller?.getBoundingClientRect();
      const axisPx = Math.max(1, rect?.height || canvasHeightPx);
      const deltaY = event.clientY - lastPointerYRef.current;
      lastPointerYRef.current = event.clientY;
      queuePanByMs(-deltaY * (durationMs / axisPx));
    };

    const handleMouseUp = () => {
      isPanningRef.current = false;
      if (scrollerRef.current) {
        scrollerRef.current.style.cursor = "";
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [canvasHeightPx, durationMs, queuePanByMs]);

  const selectedRow = primaryIndex >= 0 ? rowByPhotoIndex.get(primaryIndex) : null;
  const hasSelectedTimelinePhoto = Boolean(selectedRow);
  const selectedRowUsesHour =
    selectedRow?.bucketUnit === "minute" || selectedRow?.bucketUnit === "hour";
  const panelDate = selectedRow
    ? format(selectedRow.bucketDate, "yyyy-MM-dd")
    : calendarSelectedDate || format(new Date(), "yyyy-MM-dd");
  const panelHour = selectedRow
    ? selectedRowUsesHour
      ? selectedRow.bucketDate.getHours()
      : null
    : calendarSelectedHour;
  const filesForSelection = useMemo(() => {
    if (selectedRow) {
      return selectedRow.photos.map((photo) => ({
        file: photo.file,
        index: photo.index,
      }));
    }
    return [];
  }, [selectedRow]);
  const rightPanelShowButton =
    !isMobile && !calendarRightPanelOpen ? (
      <HeaderIconButton
        intent="utility"
        onClick={() => setCalendarRightPanelOpen(true)}
        aria-label={
          hasSelectedTimelinePhoto ? "Show assistant panel" : "Show date/time panel"
        }
        title={
          hasSelectedTimelinePhoto ? "Show assistant panel" : "Show date/time panel"
        }
      >
        <PanelRightOpen data-icon="inline-start" className="h-4 w-4" />
      </HeaderIconButton>
    ) : null;
  const getDisplayRowTopPx = useCallback(
    (row) =>
      Number.isFinite(row.topPx) ? row.topPx : getTimestampTopPx(row.bucketMs),
    [getTimestampTopPx],
  );
  const isSelectedDisplayRow = useCallback(
    (row) =>
      Boolean(
        selectedRow &&
          (row.id === selectedRow.id ||
            row.sourceRows?.some((sourceRow) => sourceRow.id === selectedRow.id)),
      ),
    [selectedRow],
  );

  const centerPane = (
    <div
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex-1 flex flex-col bg-bg-workspace overflow-hidden outline-none"
    >
      <PanelHeader
        tone="contrast"
        className="border-b-border-subtle"
        title={<TimelineV3Header range={range} />}
      >
        <div className="flex items-center gap-2">
          <TimelineV3HeaderRightControls
            zoomValue={sliderValue}
            zoomMarkers={presetMarkers}
            onZoomValueChange={(nextValue) =>
              setSliderValue(
                nextValue,
                new Date((range.start.getTime() + range.end.getTime()) / 2),
              )
            }
            onToday={goToday}
            onFit={fitToPhotos}
            onOpenShortcuts={() => {
              setKeyboardShortcutsInitialView("timelineV3");
              setKeyboardShortcutsModalOpen(true);
            }}
          />
          {rightPanelShowButton}
        </div>
      </PanelHeader>

      <div
        ref={scrollerRef}
        onWheel={handleTimelineWheel}
        onMouseDown={handleTimelineMouseDown}
        className="scrollbar-none overscroll-contain relative flex-1 cursor-grab overflow-x-auto overflow-y-hidden"
      >
        <div className="relative z-10 min-w-max p-3">
          <div
            ref={timelineTrackRef}
            className="relative flex min-w-[960px] overflow-visible rounded-md bg-bg-primary"
            style={{ height: `${canvasHeightPx}px` }}
            onDragOver={handleTimelineDragOver}
            onDragLeave={handleTimelineDragLeave}
            onDrop={handleTimelineDrop}
          >
            {dropState.active && dropState.positionPct !== null ? (
              <div
                className="pointer-events-none absolute inset-x-0 z-40 h-px bg-accent-primary shadow-[0_0_10px_var(--color-accent-primary-glow)]"
                style={{ top: `${dropState.positionPct}%` }}
              >
                <div className="absolute left-2 top-1 rounded-sm bg-accent-primary px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-text-on-accent">
                  {formatDropTimestamp(dropState.snappedDate, activePreset?.assignmentSnap)}
                </div>
              </div>
            ) : null}
            <TimelineV3TimeRail
              axisModel={axisModel}
              rows={displayRows}
              canvasHeightPx={canvasHeightPx}
              rowHeight={rowHeight}
              todayTopPx={todayTopPx}
              activeRowId={selectedRow?.id}
              getTimestampTopPx={getTimestampTopPx}
              onTimelineDragOver={handleTimelineDragOver}
              onTimelineDrop={handleTimelineDrop}
              onRowSelect={syncPanelToRow}
            />
            <div className="relative min-w-[720px] flex-1">
              {displayRows.map((row) => {
                const selected = isSelectedDisplayRow(row);
                return (
                  <div
                    key={`timeline-v3-row-band-${row.id}`}
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-x-0 ${
                      selected
                        ? "bg-bg-selected"
                        : "bg-bg-tertiary/55"
                    }`}
                    style={{
                      top: `${getDisplayRowTopPx(row)}px`,
                      height: `${Math.max(RAIL_ROW_LABEL_MIN_HEIGHT_PX, rowHeight)}px`,
                      transform: "translateY(-50%)",
                    }}
                  />
                );
              })}
              {visibleRows.length > 0 ? (
                displayRows.map((row) => (
                  <TimelineV3Row
                    key={row.id}
                    row={row}
                    topPx={getDisplayRowTopPx(row)}
                    thumbnailSize={thumbnailSize}
                    rowHeight={rowHeight}
                    selectedIndices={selectedIndices}
                    dragOverPhotoIndex={dragOverPhotoIndex}
                    onFileDragStart={handleFileDragStart}
                    onFileDragEnd={handleFileDragEnd}
                    onSelectFile={selectPhoto}
                    onEditPhoto={handleEditPhoto}
                    onRowSelect={syncPanelToRow}
                    onTimelineDragOver={handleTimelineDragOver}
                    onPhotoDragOver={handlePhotoDragOver}
                    onPhotoDragLeave={handlePhotoDragLeave}
                    onPhotoDrop={handlePhotoDrop}
                  />
                ))
              ) : (
                <div className="flex h-full min-h-[240px] items-center justify-center px-8 text-sm text-text-supporting">
                  No dated photos in this timeline range.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const rightPane = hasSelectedTimelinePhoto ? (
    <AssistantPane
      onHideDesktopRight={() => {
        setCalendarRightPanelOpen(false);
        setCalendarMobileRightOpen(false);
      }}
    />
  ) : (
    <TimePickerContent
      key={`${panelDate}-${panelHour ?? "all"}-timeline-v3`}
      date={panelDate}
      hour={panelHour}
      timezone={timezone}
      files={filesForSelection}
      onClose={() => {
        setCalendarRightPanelOpen(false);
        setCalendarMobileRightOpen(false);
        setCalendarPanelExpanded(false);
      }}
      inSheet={isMobile}
    />
  );

  if (isMobile) return null;

  return (
    <main className="relative flex h-full flex-1 flex-col bg-linear-to-br from-bg-primary to-bg-deep">
      <ThreePaneWorkspace
        isMobile={isMobile}
        leftPane={
          <StandardQueuePane
            horizontal={isMobile}
            headerContent="Files"
            showListControls={true}
          />
        }
        centerPane={centerPane}
        rightPane={rightPane}
        desktopRightOpen={calendarRightPanelOpen}
        mobileLeftOpen={calendarMobileLeftOpen}
        onMobileLeftOpenChange={setCalendarMobileLeftOpen}
        mobileRightOpen={calendarMobileRightOpen}
        onMobileRightOpenChange={setCalendarMobileRightOpen}
        mobileLeftSheetSide="bottom"
        mobileRightSheetSide="bottom"
      />
    </main>
  );
}

export default React.memo(TimelineV3View);
